import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, Trash2, Upload, Search, Filter, FolderOpen, File, Image, FileSpreadsheet, ChevronRight, X, AlertCircle } from 'lucide-react';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import { EmptyState } from '../components/design-system/EmptyState';

interface Document {
  id: string;
  propertyId: number;
  propertyAddress: string;
  tenantId: number;
  tenantName: string;
  leaseId: number;
  category: string;
  filename: string;
  url: string;
  uploadedBy: string;
  uploaderName: string;
  uploaderRole: string;
  uploadedAt: string;
  fileSize: number;
  mimeType: string;
  description: string;
}

const CATEGORIES = [
  { value: 'ALL', label: 'All Documents' },
  { value: 'LEASE', label: 'Lease Agreements' },
  { value: 'INSPECTION', label: 'Inspection Reports' },
  { value: 'RECEIPT', label: 'Payment Receipts' },
  { value: 'MAINTENANCE', label: 'Maintenance Records' },
  { value: 'NOTICE', label: 'Notices' },
  { value: 'OTHER', label: 'Other' }
];

export default function DocumentVault() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);

  useEffect(() => {
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get<Document[]>('/documents');
      setDocuments(response || []);
    } catch (err: any) {
      console.error('Failed to load documents:', err);
      // Show empty state instead of error for new users
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/documents/${id}`);
      setDocuments(prev => prev.filter(d => d.id !== id));
      setSelectedDocument(null);
    } catch (err: any) {
      setError('Failed to delete document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFileIcon = (mimeType: string, filename: string) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-8 h-8 text-blue-500" />;
    if (mimeType?.includes('spreadsheet') || filename?.endsWith('.xlsx') || filename?.endsWith('.csv')) {
      return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
    }
    if (mimeType?.includes('pdf')) return <FileText className="w-8 h-8 text-red-500" />;
    return <File className="w-8 h-8 text-gray-500" />;
  };

  const getCategoryLabel = (category: string) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat?.label || category;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'LEASE': return 'bg-blue-100 text-blue-800';
      case 'INSPECTION': return 'bg-purple-100 text-purple-800';
      case 'RECEIPT': return 'bg-green-100 text-green-800';
      case 'MAINTENANCE': return 'bg-orange-100 text-orange-800';
      case 'NOTICE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = !searchQuery ||
      doc.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.propertyAddress?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = selectedCategory === 'ALL' || doc.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <PageHeader
        title="Document Vault"
        subtitle="Access and manage your rental documents"
        icon={<FolderOpen className="w-8 h-8" />}
        backPath="/dashboard"
      >
        <button
          onClick={() => navigate('/documents/upload')}
          className="flex items-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors"
        >
          <Upload className="w-5 h-5" />
          Upload
        </button>
      </PageHeader>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-red-800">{error}</div>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-secondary focus:border-transparent"
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <EmptyState
          icon={<FolderOpen size={40} />}
          title={documents.length === 0 ? 'No documents yet' : 'No matching documents'}
          description={
            documents.length === 0
              ? 'Your lease agreements, receipts, and other documents will appear here.'
              : 'Try adjusting your search or filter criteria.'
          }
          action={
            documents.length === 0 ? (
              <button
                onClick={() => navigate('/documents/upload')}
                className="inline-flex items-center gap-2 px-4 py-2 bg-forest-900 text-white rounded-10 text-sm font-medium hover:bg-forest-800 transition-colors"
              >
                <Upload size={15} />
                Upload Your First Document
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Document List */}
          <div className="lg:col-span-2 space-y-4">
            {filteredDocuments.map(doc => (
              <div
                key={doc.id}
                onClick={() => setSelectedDocument(doc)}
                className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md ${
                  selectedDocument?.id === doc.id ? 'ring-2 ring-secondary' : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {getFileIcon(doc.mimeType, doc.filename)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900 truncate">{doc.filename}</h3>
                        <p className="text-sm text-gray-500">{doc.propertyAddress}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(doc.category)}`}>
                        {getCategoryLabel(doc.category)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span>{formatFileSize(doc.fileSize)}</span>
                      <span>•</span>
                      <span>{formatDate(doc.uploadedAt)}</span>
                      <span>•</span>
                      <span>By {doc.uploaderName || 'Unknown'}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>

          {/* Document Preview */}
          <div className="lg:col-span-1">
            {selectedDocument ? (
              <div className="bg-white rounded-lg shadow p-6 sticky top-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Document Details</h3>
                  <button
                    onClick={() => setSelectedDocument(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex justify-center mb-6">
                  {getFileIcon(selectedDocument.mimeType, selectedDocument.filename)}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Filename</label>
                    <p className="font-medium text-gray-900">{selectedDocument.filename}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Category</label>
                    <p className="font-medium text-gray-900">{getCategoryLabel(selectedDocument.category)}</p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Property</label>
                    <p className="font-medium text-gray-900">{selectedDocument.propertyAddress || 'N/A'}</p>
                  </div>

                  {selectedDocument.description && (
                    <div>
                      <label className="text-xs text-gray-500 uppercase">Description</label>
                      <p className="text-gray-700">{selectedDocument.description}</p>
                    </div>
                  )}

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Uploaded</label>
                    <p className="text-gray-700">
                      {formatDate(selectedDocument.uploadedAt)} by {selectedDocument.uploaderName || 'Unknown'}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs text-gray-500 uppercase">Size</label>
                    <p className="text-gray-700">{formatFileSize(selectedDocument.fileSize)}</p>
                  </div>
                </div>

                <div className="flex gap-2 mt-6">
                  {selectedDocument.url && (
                    <a
                      href={selectedDocument.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(selectedDocument.id)}
                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Select a document to view details</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


